/**
 * Where the calendar puts each phase of a set: live, upcoming, ended, or
 * undated. Pure — `today` is always passed in, so this is unit-testable and
 * the UI decides how often "today" is re-read.
 *
 * Everything is whole local days. See `IsoDate` for why not hours.
 */

import type { IsoDate, TrackerGroup, TrackerSet } from './content.types';

export type Phase =
  /** No dates on the group: obtainable all season, never highlighted. */
  | { kind: 'always' }
  | { kind: 'upcoming'; startsIn: number }
  /** `daysLeft` is null when the phase has no `ends` — runs to season close. */
  | { kind: 'live'; daysLeft: number | null }
  | { kind: 'ended' };

const DAY_MS = 86_400_000;

/** The calendar date where the user is, not in UTC — a phase that starts on
 *  the 29th should read as live on the 29th in Vladivostok and in Lisbon. */
export function localIsoDate(date: Date = new Date()): IsoDate {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}` as IsoDate;
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  // UTC arithmetic on purpose: no DST hour can make a day 23 or 25 hours long.
  return Math.round((Date.UTC(...parts(to)) - Date.UTC(...parts(from))) / DAY_MS);
}

function parts(date: IsoDate): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y ?? 0, (m ?? 1) - 1, d ?? 1];
}

export function phaseOf(group: Pick<TrackerGroup, 'starts' | 'ends'>, today: IsoDate): Phase {
  if (!group.starts) return { kind: 'always' };
  const untilStart = daysBetween(today, group.starts);
  if (untilStart > 0) return { kind: 'upcoming', startsIn: untilStart };
  if (!group.ends) return { kind: 'live', daysLeft: null };
  const untilEnd = daysBetween(today, group.ends);
  // `ends` is exclusive, so on the last day `untilEnd` is 1, never 0.
  if (untilEnd <= 0) return { kind: 'ended' };
  return { kind: 'live', daysLeft: untilEnd };
}

export interface Schedule {
  /**
   * Dated groups live today, newest start first. Phases pile up rather than
   * replace each other, so by the end of the season this is most of them and
   * the freshest one is what the card should lead with.
   */
  live: { group: TrackerGroup; daysLeft: number | null }[];
  /** The soonest phase still to come, if any. */
  next: { group: TrackerGroup; startsIn: number } | null;
  /**
   * `before`: nothing has started. `after`: every dated phase has ended — which
   * can never happen while a phase is open-ended. `during` otherwise, including
   * a gap between two phases.
   */
  state: 'before' | 'during' | 'after';
}

/** Null when the set carries no dates at all, so the UI can skip the card. */
export function selectSchedule(set: TrackerSet, today: IsoDate): Schedule | null {
  const dated = set.groups.filter((g) => g.starts !== undefined);
  if (dated.length === 0) return null;

  const live: Schedule['live'] = [];
  let next: Schedule['next'] = null;
  let ended = 0;
  for (const group of dated) {
    const phase = phaseOf(group, today);
    if (phase.kind === 'live') live.push({ group, daysLeft: phase.daysLeft });
    else if (phase.kind === 'ended') ended += 1;
    else if (phase.kind === 'upcoming' && (!next || phase.startsIn < next.startsIn)) {
      next = { group, startsIn: phase.startsIn };
    }
  }

  live.sort((a, b) => daysBetween(a.group.starts ?? today, b.group.starts ?? today));

  const state: Schedule['state'] =
    ended === dated.length ? 'after' : live.length === 0 && ended === 0 ? 'before' : 'during';
  return { live, next, state };
}
